/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  CircleCIOptions,
  BuildWithSteps,
  BuildStepAction,
  BuildSummary,
  GitType,
} from 'circleci-api';
import { createApiRef, DiscoveryApi, IdentityApi } from '@backstage/core-plugin-api';

/** @public */
export { GitType };

/** @public */
export type { BuildWithSteps, BuildStepAction, BuildSummary };

/** @public */
export const circleCIApiRef = createApiRef<CircleCIApi>({
  id: 'plugin.circleci.service',
});

const DEFAULT_PROXY_PATH = '/circleci/api';

/** @public */
export class CircleCIApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;
  private readonly proxyPath: string;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
    /**
     * Path to use for requests via the proxy, defaults to /circleci/api
     */
    proxyPath?: string;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
    this.proxyPath = options.proxyPath ?? DEFAULT_PROXY_PATH;
  }

  async retry(buildNumber: number, options: Partial<CircleCIOptions>) {
    const vcsType = options.vcs?.type || 'bitbucket';
    const owner = options.vcs?.owner || '';
    const repo = options.vcs?.repo || '';
    const url = `${await this.getApiUrl()}/project/${vcsType}/${owner}/${repo}/${buildNumber}/retry`;
    const requestOptions = await this.getRequestOptions();
    
    const response = await fetch(url, {
      method: 'POST',
      ...requestOptions,
    });
    
    if (!response.ok) {
      throw new Error(`Failed to retry build: ${response.status}`);
    }
    
    return response.json();
  }

  async getBuilds(
    pagination: { limit: number; offset: number },
    options: Partial<CircleCIOptions>,
  ) {
    const { limit = 10, offset = 0 } = pagination;
    const vcsType = options.vcs?.type || 'bitbucket';
    const owner = options.vcs?.owner || '';
    const repo = options.vcs?.repo || '';
    
    const url = `${await this.getApiUrl()}/project/${vcsType}/${owner}/${repo}?limit=${limit}&offset=${offset}`;
    const requestOptions = await this.getRequestOptions();
    
    const response = await fetch(url, requestOptions);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch builds: ${response.status}`);
    }
    
    return response.json();
  }

  async getUser(_options?: Partial<CircleCIOptions>) {
    const url = `${await this.getApiUrl()}/me`;
    const requestOptions = await this.getRequestOptions();
    
    const response = await fetch(url, requestOptions);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch user: ${response.status}`);
    }
    
    return response.json();
  }

  async getBuild(buildNumber: number, options: Partial<CircleCIOptions>) {
    const vcsType = options.vcs?.type || 'bitbucket';
    const owner = options.vcs?.owner || '';
    const repo = options.vcs?.repo || '';
    
    const url = `${await this.getApiUrl()}/project/${vcsType}/${owner}/${repo}/${buildNumber}`;
    const requestOptions = await this.getRequestOptions();
    
    const response = await fetch(url, requestOptions);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch build: ${response.status}`);
    }
    
    return response.json();
  }

  private async getApiUrl() {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    return proxyUrl + this.proxyPath;
  }

  private async getRequestOptions(): Promise<RequestInit> {
    const { token } = await this.identityApi.getCredentials();
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };
  }
}
